import { postFirmarCadena } from 'api/efirma';
import { store } from 'store';
import { SET_TIPOS_DE_PASOS_DE_TRAMITES } from 'store/reducers/catalogs';
import API, { createEmptyAxiosInstance } from 'utils/api';
import { onError } from 'utils/handlers';
import { formatReceived } from 'utils/formatValues';
import { postHistorialDeTramite } from './historial';

/**
 * GET Tipos de Oficinas de Atención
 * @returns {Promise<object[]>}
 */
export const getTiposDeOficinasDeAtencion = async () => {
  try {
    const response = await API.get('/tramites/tipos-de-oficinas-de-atencion/');
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *  GET Plantillas de Trámites
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getPlantillasDeTramite = async (params) => {
  try {
    const response = await API.get('/tramites/plantillas-tramites-atencion-ciudadana/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return params.page ? { results: [] } : [];
  }
};

/**
 *  GET Plantillas de Trámites
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getPlantillasDeTramitePortal = async (params, cancelToken) => {
  try {
    const response = await API.get('/tramites/plantillas-de-tramites-portal/', { params, cancelToken });
    return response.data;
  } catch (err) {
    onError(err);
    return params.page ? { results: [] } : [];
  }
};

/**
 *  GET Tipos de Requisitos
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getTiposDeRequisitos = async (params) => {
  try {
    const response = await API.get('/tramites/tipos-de-requisitos/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *  GET Categorias de Requisitos
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getCategoriasDeRequisitos = async (params) => {
  try {
    const response = await API.get('/catalogos/categorias-para-tramites/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *  GET Unidades de plazo
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getUnidadesDePlazo = async (params) => {
  try {
    const response = await API.get('/catalogos/unidades-de-plazo/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *  GET Catalogo de Requisitos
 * @param {number[]} ids
 * @returns {Promise<object[]>}
 */
export const getManyCatalogosDeRequisitos = async (_ids) => {
  try {
    const ids = [...new Set(_ids)];
    const responses = await Promise.all(ids.map((id) => API.get(`/catalogos/catalogos-de-requisitos/${id}`)));
    return responses.map(({ data }) => data);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *  GET Requisitos
 * @param {number[]} ids
 * @returns {Promise<object[]>}
 */
export const getManyRequisitos = async (_ids) => {
  try {
    const ids = [...new Set(_ids)];
    const responses = await Promise.all(ids.map((id) => API.get(`/tramites/requisitos/${id}`)));
    return responses.map(({ data }) => data);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *  GET Tipos de Pasos de Tramites
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getTiposDePasosDeTramites = async (params) => {
  try {
    const response = await API.get('/tramites/tipos-de-pasos-de-tramites/', { params });
    store.dispatch({
      type: SET_TIPOS_DE_PASOS_DE_TRAMITES,
      payload: response.data,
    });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 *  POST Firmar de Tramites
 * @param {object} tramite
 * @param {object} values
 * @param {object} valuesLog
 * @returns {Promise<object>}
 */
export const patchFirmarTramite = async (tramite, values, valuesLog) => {
  const firmasElectronicas = JSON.parse(tramite.firma_electronica || '[]');
  const firma = await postFirmarCadena(values, tramite);
  if (!firma) return null;
  firma.paso = values.paso;
  const firma_electronica = [...firmasElectronicas.map((f) => ({
    ...f,
    eliminado: f.paso === firma.paso,
  })), firma];
  const valuesTramite = { firma_electronica: JSON.stringify(firma_electronica) };
  try {
    const response = await API.patch(`/tramites/tramites/${tramite.id}`, valuesTramite);
    if (valuesLog) {
      await postHistorialDeTramite(valuesLog);
    }
    return response.data;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 *  GET Pasos De Tramites
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getPasosDeTramites = async (params) => {
  try {
    const response = await API.get('/tramites/pasos-de-tramites/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Expedientes De Tramites
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getTiposDeDocumentosParaExpedientes = async (params) => {
  try {
    const response = await API.get('empresas/tipos-de-documentos-para-expedientes', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Plazos Máximos
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getPlazosMaximos = async (params) => {
  try {
    const response = await API.get('tramites/plazos-maximos/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET ZIP Expediente digital
 * @param {number} id
 * @returns {Promise<string|null>}
 */
export const getZipExpediente = async (id) => {
  try {
    const response = await API.get(`tramites/tramite-expediente/${id}/zip`);
    return `data:application/zip;base64,${response.data}`;
  } catch (err) {
    onError(err);
    return null;
  }
};

/**
 * GET Dias No Laborales
 * @param {object} params
 * @returns {Promise<object[]>}
 */
export const getDiasNoLaborales = async (params) => {
  try {
    const response = await API.get('tramites/dias-no-laborales-public', { params });
    return formatReceived(response.data);
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Ventanillas
 * @param {object} params
 * @returns {Promise<string|null>}
 */
export const getVentanillas = async (params) => {
  try {
    const response = await API.get('tramites/ventanillas/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Estados citas
 * @param {object} params
 * @returns {Promise<string|null>}
 */
export const getEstadosCitas = async (params) => {
  try {
    const response = await API.get('recaudacion/estatus-de-las-citas/', { params });
    return response.data;
  } catch (err) {
    onError(err);
    return [];
  }
};

/**
 * GET Estatus REPUVE
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getEstatusRepuve = async (params = null) => {
  try {
    const response = await API.get('tramites/estatus-de-envio-ws-repuve/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * GET Estatus REPUVE
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getEstatusCI = async (params = null) => {
  try {
    const response = await API.get('recaudacion/estatus-constancias-de-inscripciones/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * GET Logs WS
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getLogsWS = async (params = null) => {
  try {
    const response = await API.get('tramites/logs-ws-repuve/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * GET Consulta trámite finanzas
 * @param {string} folio
 * @returns {Promise<object|false>}
 */
export const consultaTramiteFinanzas = async (folio) => {
  try {
    const response = await createEmptyAxiosInstance()
      .post('tramites/tramites-repuve/tramites-folios/', { folio });
    return response.data[0];
  } catch (error) {
    onError(error);
    return false;
  }
};

/**
 * GET Catalogo de Codigos de Cierre
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getCatalogoCodigosDeCierre = async (params = null) => {
  try {
    const response = await API.get('tramites/catalogos-de-codigos-de-cierre/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * GET Catalogo de Origen de Registro
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getCatalogoDeOrigenRegistro = async (params = null) => {
  try {
    const response = await API.get('tramites/catalogos-origen-registro/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * GET Catalogo de Estatus de Registro
 * @param {Object} params
 * @returns {Promise<object[]>}
 */
export const getEstatusDeRegistro = async (params = null) => {
  try {
    const response = await API.get('tramites/catalogos-estatus-registro/', { params });
    return response.data;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * @param {Object} values
 * @param {Object} values.predio
 * @param {Object[]} values.participes
 * @param {Object} values.direccion
 * @param {Object} values.tramite
 * @param {Object} values.complemento
 * @param {string[]} values.expedientes
 * @returns {Promise<Object[]>}
 */
export const postTramitesHijas = async (values) => {
  try {
    const response = await API.post('tramites/subdivision-de-predio/', values);
    return response.data.tramites;
  } catch (error) {
    onError(error);
    return [];
  }
};

/**
 * @param {Object} values
 * @param {number[]} values.object_id_padron
 * @param {number} values.plantilla_id
 * @param {number} values.content_type_a_padron
 * @param {boolean} values.solicitante_ciudadano
 * @param {number} values.contribuyente_id
 * @param {number} values.tipos_de_registros_de_tramite_id
 * @param {number} values.estados_globales_id
 * @param {string} values.fecha_de_inicio
 * @param {string} values.procedencia_id
 * @param {boolean} values.aplica_avaluo
 * @returns {Promise<Object[]>}
 */
export const postTramitesMasivo = async (values) => {
  try {
    const response = await API.post('/tramites/importar-masivo/', values);
    return response.data.tramites;
  } catch (error) {
    onError(error);
    return [];
  }
};

export const getHistorialTramitesPrevios = async (params) => {
  try {
    const response = await API.get('/tramites/historial-de-tramites-predio/', { params });
    return response.data.map((e) => ({
      ...formatReceived(e),
      area_titulo: e.area_titulo?.toFixed(4),
      distancia_esquina: e.distancia_esquina?.toFixed(4),
      frente: e.frente?.toFixed(4),
      perimetro: e.perimetro?.toFixed(4),
      profundidad: e.profundidad?.toFixed(4),
      superficie_construccion: e.superficie_construccion?.toFixed(4),
      superficie_terreno: e.superficie_terreno?.toFixed(4),
      valor_construccion: e.valor_construccion?.toFixed(4),
      valor_fiscal: e.valor_fiscal?.toFixed(4),
      valor_terreno: e.valor_terreno?.toFixed(4),
    }));
  } catch (error) {
    onError(error);
    return [];
  }
};
